import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';

import { Observable, Subscription } from 'rxjs';

import { unsubscribe } from '@fcom/core/utils';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';

type SvgWingGeometry = { transform: string; points: string };

@Component({
  selector: 'fin-seat-map-mini-map',
  templateUrl: './seat-map-mini-map.component.html',
  styleUrls: ['./seat-map-mini-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SeatMapMiniMapComponent implements OnDestroy, OnInit, OnChanges, AfterViewInit {
  private gtmService = inject(GtmService);

  @Input() viewPortWidth$: Observable<number | undefined>;
  @Input() scrollPosition: number;
  @Input() svgId: string;
  @Input() seatMapSvg: string;

  @Output() scrollTo: EventEmitter<number> = new EventEmitter();
  @Output() keyScrollDirection: EventEmitter<'left' | 'right'> = new EventEmitter();

  @ViewChild('selectedArea') selectedArea: ElementRef;
  @ViewChild('seatMapContainer') seatMapContainer: ElementRef;
  @ViewChild('wrapper') wrapper: ElementRef;

  readonly leftWingClass = 'left-wing';
  readonly rightWingClass = 'right-wing';

  svgWidth: number;
  svgViewBox: string;
  svgLeftWing: SvgWingGeometry;
  svgRightWing: SvgWingGeometry;
  subscription = new Subscription();

  ngOnInit(): void {
    this.initializeSvgSizes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.scrollPosition) {
      this.setNewAreaPosition();
    }
  }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.viewPortWidth$.subscribe((viewPortWidth) => {
        if (!viewPortWidth) {
          return;
        }
        const container = this.seatMapContainer.nativeElement;
        const visibleAreaPercentage = viewPortWidth / this.svgWidth;
        this.selectedArea.nativeElement.style.width = `${container.offsetWidth * visibleAreaPercentage}px`;
      })
    );

    this.setNewAreaPosition();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  handleClick(e: MouseEvent): void {
    const parentWidth = this.seatMapContainer.nativeElement.offsetWidth;
    const parentLeft = this.seatMapContainer.nativeElement.getBoundingClientRect().left;
    const areaWidth = this.selectedArea.nativeElement.offsetWidth;

    const newPosition = e.clientX - parentLeft - areaWidth / 2;
    this.setSelectedAreaPosition(newPosition);

    this.scrollTo.emit((Math.max(0, newPosition) / parentWidth) * this.svgWidth);
    this.gtmService.trackElement('seatmap-mini', GaContext.SEAT_MAP, ElementTypes.BUTTON, '', ElementActions.CLICK);
  }

  setSelectedAreaPosition(newPosition: number): void {
    const parentWidth = this.seatMapContainer.nativeElement.offsetWidth;
    const areaWidth = this.selectedArea.nativeElement.offsetWidth;

    const parentBorder = (this.wrapper.nativeElement.offsetWidth - this.wrapper.nativeElement.clientWidth) / 2;
    const totalBorderWidth = this.getTotalBorderWidth();
    const minPosition = totalBorderWidth * -1;

    if (newPosition < minPosition) {
      newPosition = minPosition;
    }
    if (newPosition + areaWidth > parentWidth + totalBorderWidth) {
      newPosition = parentWidth - areaWidth + (totalBorderWidth / 2 + parentBorder);
    }

    requestAnimationFrame(() => {
      this.selectedArea.nativeElement.style.left = `${newPosition}px`;
    });
  }

  getTotalBorderWidth(): number {
    if (!this.selectedArea) {
      return 0;
    }

    const selectedAreaBorder =
      (this.selectedArea.nativeElement.offsetWidth - this.selectedArea.nativeElement.clientWidth) / 2;
    const parentBorder = (this.wrapper.nativeElement.offsetWidth - this.wrapper.nativeElement.clientWidth) / 2;

    return selectedAreaBorder + parentBorder;
  }

  setNewAreaPosition(): void {
    const borderWidth = this.getTotalBorderWidth();

    requestAnimationFrame(() => {
      let newPosition = (this.scrollPosition / this.svgWidth) * this.seatMapContainer.nativeElement.offsetWidth;

      if (newPosition === 0) {
        newPosition = -borderWidth;
      }

      this.setSelectedAreaPosition(newPosition);
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') {
      this.keyScrollDirection.emit('left');
    } else if (event.key === 'ArrowLeft') {
      this.keyScrollDirection.emit('right');
    }
  }

  clickFront(): void {
    this.scrollTo.emit(0);
  }

  clickBack(): void {
    this.scrollTo.emit(this.svgWidth);
  }

  private initializeSvgSizes(): void {
    const container = document.createElement('div');
    container.innerHTML = this.seatMapSvg;
    const svg = container.querySelector('svg');

    this.svgViewBox = svg.getAttribute('viewBox');
    this.svgWidth = Number(svg.getAttribute('data-optimal-width'));

    this.svgRightWing = this.readWing(svg, `g.wing.${this.rightWingClass}`);
    this.svgLeftWing = this.readWing(svg, `g.wing.${this.leftWingClass}`);
  }

  private readWing(svg: SVGSVGElement, groupSelector: string): SvgWingGeometry {
    const g = svg.querySelector(groupSelector) as SVGGElement | null;
    const poly = g?.querySelector('polygon') as SVGPolygonElement | null;
    return {
      transform: g?.getAttribute('transform') || '',
      points: poly?.getAttribute('points') || '',
    };
  }
}
